import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from "react";
import { LogEntry, LogLevel } from "../log/logComponent.js";
import { LineContainerComponent } from "../../sharedComponents/lineContainerComponent.js";
import { ButtonLineComponent } from "shared-ui-components/lines/buttonLineComponent";
import { TextInputLineComponent } from "shared-ui-components/lines/textInputLineComponent";
import { TextLineComponent } from "shared-ui-components/lines/textLineComponent";
import { CloseMcpEditorSessionEventSource, NormalizeMcpEditorSessionUrl, OpenMcpEditorSessionEventSource, PostMcpEditorSessionDocumentAsync, } from "shared-ui-components/mcp/mcpEditorSessionConnection";
/**
 * Panel that connects to a live MCP session for bidirectional Smart Filter sync.
 * @param props - Component props.
 * @returns The React element.
 */
export const McpSessionComponent = (props) => {
    const { globalState } = props;
    const [url, setUrl] = useState(globalState.mcpSessionUrl ?? "");
    const [connected, setConnected] = useState(globalState.mcpSessionConnected);
    useEffect(() => {
        const observer = globalState.onMcpSessionStateChangedObservable.add((state) => {
            setConnected(state);
        });
        setConnected(globalState.mcpSessionConnected);
        if (globalState.mcpSessionUrl) {
            setUrl(globalState.mcpSessionUrl);
        }
        return () => {
            globalState.onMcpSessionStateChangedObservable.remove(observer);
        };
    }, [globalState]);
    const logError = useCallback((message) => {
        globalState.onLogRequiredObservable.notifyObservers(new LogEntry(message, LogLevel.Error));
    }, [globalState]);
    const loadSmartFilterFromJson = useCallback((json) => {
        void (async () => {
            if (!globalState.pasteSmartFilterFromStringAsync) {
                logError("MCP Session: Loading is not available in this host.");
                return;
            }
            try {
                if (await globalState.pasteSmartFilterFromStringAsync(JSON.stringify(json))) {
                    globalState.stateManager.onSelectionChangedObservable.notifyObservers(null);
                    globalState.onClearUndoStack.notifyObservers();
                }
            }
            catch (err) {
                logError(`MCP Session: Load failed - ${err}`);
            }
        })();
    }, [globalState, logError]);
    const pushSmartFilterAsync = useCallback(async (sessionUrl) => {
        if (!globalState.copySmartFilterToStringAsync) {
            logError("MCP Session: Pushing is not available in this host.");
            return;
        }
        globalState.onSaveEditorDataRequiredObservable.notifyObservers();
        const serializedSmartFilter = await globalState.copySmartFilterToStringAsync();
        const res = await PostMcpEditorSessionDocumentAsync(sessionUrl, serializedSmartFilter);
        if (!res.ok) {
            logError(`MCP Session: Push failed (${res.status})`);
        }
    }, [globalState, logError]);
    const handleConnect = useCallback(async (pushOnConnect = false) => {
        const sessionUrl = NormalizeMcpEditorSessionUrl(url);
        if (!sessionUrl) {
            return;
        }
        try {
            if (pushOnConnect) {
                await pushSmartFilterAsync(sessionUrl);
            }
            CloseMcpEditorSessionEventSource(globalState.mcpEventSource);
            globalState.mcpEventSource = null;
            const eventSource = OpenMcpEditorSessionEventSource({
                sessionUrl,
                onDocument: loadSmartFilterFromJson,
                onSessionClosed: (reason) => {
                    globalState.onLogRequiredObservable.notifyObservers(new LogEntry(`MCP Session ended: ${reason}`, LogLevel.Log));
                    globalState.mcpSessionConnected = false;
                    globalState.mcpEventSource = null;
                    globalState.onMcpSessionStateChangedObservable.notifyObservers(false);
                },
                onConnectionError: () => {
                    globalState.mcpSessionConnected = false;
                    globalState.mcpEventSource = null;
                    globalState.onMcpSessionStateChangedObservable.notifyObservers(false);
                },
            });
            globalState.mcpEventSource = eventSource;
            globalState.mcpSessionUrl = sessionUrl;
            globalState.mcpSessionConnected = true;
            globalState.onMcpSessionStateChangedObservable.notifyObservers(true);
        }
        catch (err) {
            logError(`MCP Session: Connection failed - ${err}`);
        }
    }, [url, globalState, loadSmartFilterFromJson, logError, pushSmartFilterAsync]);
    const handleDisconnect = useCallback(() => {
        CloseMcpEditorSessionEventSource(globalState.mcpEventSource);
        globalState.mcpEventSource = null;
        globalState.mcpSessionConnected = false;
        globalState.mcpSessionUrl = null;
        globalState.onMcpSessionStateChangedObservable.notifyObservers(false);
    }, [globalState]);
    const handlePush = useCallback(async () => {
        if (!globalState.mcpSessionUrl) {
            return;
        }
        try {
            await pushSmartFilterAsync(globalState.mcpSessionUrl);
        }
        catch (err) {
            logError(`MCP Session: Push failed - ${err}`);
        }
    }, [globalState.mcpSessionUrl, logError, pushSmartFilterAsync]);
    return (_jsxs(LineContainerComponent, { title: "MCP SESSION", closed: true, children: [_jsx(TextInputLineComponent, { label: "Session URL", value: url, onChange: (value) => setUrl(value), placeholder: "http://localhost:3001/session/...", disabled: connected, lockObject: globalState.lockObject }), _jsx(TextLineComponent, { label: "Status", value: connected ? "Connected" : "Disconnected", color: connected ? "#4caf50" : "#888" }), !connected ? (_jsxs(_Fragment, { children: [_jsx(ButtonLineComponent, { label: "Connect", onClick: () => {
                            void handleConnect(false);
                        } }), _jsx(ButtonLineComponent, { label: "Connect & Push", onClick: () => {
                            void handleConnect(true);
                        } })] })) : (_jsxs(_Fragment, { children: [_jsx(ButtonLineComponent, { label: "Disconnect", onClick: handleDisconnect }), _jsx(ButtonLineComponent, { label: "Push to MCP", onClick: () => {
                            void handlePush();
                        } })] }))] }));
};
//# sourceMappingURL=mcpSessionComponent.js.map